import { createContext, useContext, type ReactNode } from 'react';
import { PROFILE_LAYOUT_STRINGS as L } from '@/lib/constants/profile-layout-strings';

type Columns = 1 | 2 | 3 | 4;

/**
 * True on a page that hides empty fields instead of saying "Not provided" -
 * the shared view-only startup page (FR-180). Cards read it too, to drop rows
 * such as a missing pitch video.
 */
export const ProfileHideEmptyContext = createContext(false);

// Literal class names: Tailwind only generates classes it can find verbatim.
// Phones get one column; two from `sm`; the full count from `md`.
const COLUMNS: Record<Columns, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-1 sm:grid-cols-2',
  3: 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3',
  4: 'grid-cols-1 sm:grid-cols-2 md:grid-cols-4',
};

const SPAN: Record<Columns, string> = {
  1: '',
  2: 'sm:col-span-2',
  3: 'sm:col-span-2 md:col-span-3',
  4: 'sm:col-span-2 md:col-span-4',
};

export interface ProfileFactsProps {
  columns: Columns;
  children: ReactNode;
}

/** The label-over-value grid inside a profile card (mockup `dl`: 18px rows, 20px columns). */
export function ProfileFacts({ columns, children }: ProfileFactsProps) {
  return <dl className={`m-0 grid gap-x-5 gap-y-[1.125rem] ${COLUMNS[columns]}`}>{children}</dl>;
}

export interface ProfileFactProps {
  label: string;
  /** Plain value; empty shows "Not provided" in muted text. Ignored when `children` is given. */
  value?: string | number | null;
  /** Rich value (chips, a link). */
  children?: ReactNode;
  /** Columns this fact spans in its `ProfileFacts` grid. */
  span?: Columns;
  /** Long-form text: regular weight, 23px line height (Use of funds). */
  prose?: boolean;
  /** Override the empty text, e.g. "Not confirmed". */
  emptyText?: string;
  /** One line with an ellipsis (emails, links); the full text shows on hover. */
  truncate?: boolean;
}

/** One label/value pair: 12px muted label, 15px medium value. */
export function ProfileFact({
  label,
  value,
  children,
  span = 1,
  prose = false,
  emptyText = L.NOT_PROVIDED,
  truncate = false,
}: ProfileFactProps) {
  const hideEmpty = useContext(ProfileHideEmptyContext);
  const isEmpty = children === undefined && (value === undefined || value === null || value === '');
  if (isEmpty && hideEmpty) return null;
  const valueClass = prose ? 'leading-[1.4375rem]' : 'font-medium';
  const wrapClass = truncate ? 'truncate' : 'break-words';
  return (
    <div className={`min-w-0 ${SPAN[span]}`}>
      <dt className="text-subtext text-xs">{label}</dt>
      <dd
        title={truncate && typeof value === 'string' ? value : undefined}
        className={`text-input m-0 mt-0.5 ${wrapClass} ${isEmpty ? 'text-subtext font-medium' : `text-navy ${valueClass}`}`}
      >
        {children ?? (isEmpty ? emptyText : value)}
      </dd>
    </div>
  );
}
